import React from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import './HypeScanner.css'
import CategoryRsiSummary from "./CategoryRsiSummary";
// -------- Helpers --------
const TIME_PRESETS = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
];

function fmtDateShort(d) {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (!dt || Number.isNaN(+dt)) return "—";
    return dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function avgChange24(tokens = []) {
  const valid = tokens
    .map(t => Number(t.priceChange24h || 0))
    .filter(v => Number.isFinite(v));
  if (!valid.length) return null;
  const sum = valid.reduce((s, v) => s + v, 0);
  return +(sum / valid.length).toFixed(1);
}

// recompute "core" and "sprinters" from sampleTokens (same spirit as backend)
function computeChangeBuckets(sampleTokens = []) {
  if (!Array.isArray(sampleTokens) || !sampleTokens.length) {
    return {
      coreChange: null,
      sprChange: null,
      allChange: null,
    };
  }

  const tokens = [...sampleTokens];

  const core = [...tokens]
    .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0))
    .slice(0, 4);

  const sprinters = [...tokens]
    .sort(
      (a, b) =>
        (b.priceChange24h || 0) - (a.priceChange24h || 0)
    )
    .slice(0, 4);

  return {
    coreChange: avgChange24(core),
    sprChange: avgChange24(sprinters),
    allChange: avgChange24(tokens),
  };
}

function fmtChangePct(v) {
  if (v == null || !Number.isFinite(+v)) return "—";
  const n = Number(v);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

function changeColor(v) {
  if (v == null || !Number.isFinite(+v)) return "#e5e7eb"; // neutral slate
  const n = Number(v);

  if (n >= 10) return "#22c55e";    // strong green
  if (n >= 0)  return "#38bdf8";    // mild positive: blue
  if (n > -10) return "#f97316";    // mild negative: orange
  return "#ef4444";                 // strong red
}



function fmtNum(v, d = 2) {
  if (!Number.isFinite(+v)) return "—";
  return Number(v).toFixed(d);
}

function fmtInt(v) {
  if (!Number.isFinite(+v)) return "0";
  return Number(v).toLocaleString();
}

function rsiBandColor(rsi) {
  if (!Number.isFinite(rsi)) return "#64748b"; // slate
  if (rsi < 40) return "#22c55e";             // green
  if (rsi < 60) return "#e5e7eb";             // neutral
  if (rsi < 70) return "#f97316";             // warm
  return "#ef4444";                           // hot
}

// bubble color based on hype hits
function hitColor(hitCount) {
  const n = Number(hitCount || 0);
  if (n >= 25) return "#ef4444"; // very hot
  if (n >= 10) return "#f97316"; // warm
  if (n >= 3)  return "#22c55e"; // active
  return "#38bdf8";              // mild
}

// create a short label from category name, e.g. "Artificial Intelligence (AI)" → "AI"
function abbrevFromName(name) {
  if (!name) return "";
  const cleaned = String(name).replace(/[()]/g, " ");
  const words = cleaned.split(/\s+/).filter(Boolean);

  // if something inside parentheses like (AI), prioritize that
  const parensMatch = name.match(/\(([A-Za-z0-9]+)\)/);
  if (parensMatch && parensMatch[1]) {
    return parensMatch[1].toUpperCase().slice(0, 4);
  }

  if (words.length === 1) {
    return words[0].slice(0, 4).toUpperCase();
  }
  // first letters of first 2–3 words
  return words
    .slice(0, 3)
    .map(w => w[0])
    .join("")
    .toUpperCase();
}

// TradingView chart for a token (symbol only, we append USDT/BINANCE)
function openTokenChart(symbol) {
  if (!symbol) return;
  const sym = String(symbol).toUpperCase();
  const url = `https://www.tradingview.com/chart/?symbol=BINANCE%3A${encodeURIComponent(
    sym + "USDT"
  )}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function deltaRsiColor(delta) {
  if (!Number.isFinite(delta)) return "#6b7280"; // neutral gray (Tailwind zinc-500)

  const d = Number(delta);

  // Tweak thresholds to taste
  if (d >= 3)  return "#22c55e"; // strong up
  if (d >= 1)  return "#4ade80"; // moderate up
  if (d > -1)  return "#e5e7eb"; // flat / small move
  if (d > -3)  return "#f97316"; // moderate down
  return "#ef4444";              // strong down
}


// custom bubble with abbreviation text inside
function renderBubble(props) {
  const { cx, cy, size, payload } = props;
  if (cx == null || cy == null) return null;

  const radius = Math.max(10, Math.min(30, Math.sqrt(size || 200))); // clamp
  const fill = hitColor(payload.hitCount);
  const abbr = payload.abbr || "";

  return (
    <g>
      <circle cx={cx} cy={cy} r={radius} fill={fill} fillOpacity={0.9} />
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fontWeight={600}
        fill="#020617"
      >
        {abbr}
      </text>
    </g>
  );
}

function renderPerfBubble(props) {
  const { cx, cy, size, payload } = props;
  if (cx == null || cy == null) return null;

  const radius = Math.max(10, Math.min(30, Math.sqrt(size || 200)));
  const fill = deltaRsiColor(payload.deltaRsi);
  const abbr = payload.abbr || "";

  const delta = Number(payload.deltaRsi);
  const hasDelta = Number.isFinite(delta) && delta !== 0;

  // 1 vertical line per 1% change (min 1 if |Δ| > 0)
  let numLines = 0;
  if (hasDelta) {
    numLines = Math.max(1, Math.round(Math.abs(delta)));
    numLines = Math.min(numLines, 10); // safety cap
  }

  const isUp = delta > 0; // Δ RSI > 0 = rising → lines BELOW bubble
  const lineHeight = 6;   // height of each vertical line
  const lineGap = 4;      // gap between lines
  const startOffset = radius + 4; // distance from bubble edge to first line

  const dirColor = fill; // same color theme as bubble

  return (
    <g>
      {/* Bubble */}
      <circle cx={cx} cy={cy} r={radius} fill={fill} fillOpacity={0.9} />

      {/* Bubble Label */}
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fontWeight={600}
        fill="#020617"
      >
        {abbr}
      </text>

      {/* Direction Lines (vertical bars) */}
      {hasDelta &&
        Array.from({ length: numLines }).map((_, i) => {
          const offset = startOffset + i * (lineGap + lineHeight);
          const yTop = isUp ? cy + offset : cy - offset;  // below for up, above for down
          return (
            <line
              key={i}
              x1={cx}
              x2={cx}
              y1={yTop}
              y2={yTop + (isUp ? lineHeight : -lineHeight)}
              stroke={dirColor}
              strokeWidth={2}
              strokeLinecap="round"
            />
          );
        })}
    </g>
  );
}


function CategoryTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;

  const p = payload[0].payload; // this is your scatterData point

  return (
    <div
      style={{
        background: "#020617",
        border: "1px solid #1f2937",
        borderRadius: 8,
        padding: "6px 10px",
        fontSize: 12,
        color: "#e5e7eb",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {p.name || "Unknown category"}
      </div>
      <div>Last hit: {p.lastHitAt ? fmtDateShort(p.lastHitAt) : "—"}</div>
      <div>Tokens: {fmtInt(p.tokenCount)}</div>
      <div>Hits: {fmtInt(p.hitCount)}</div>
    </div>
  );
}

function PerfTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;

  return (
    <div
      style={{
        background: "#020617",
        border: "1px solid #1f2937",
        borderRadius: 8,
        padding: "6px 10px",
        fontSize: 12,
        color: "#e5e7eb",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {p.name || "Unknown category"}
      </div>
      <div>All RSI: {fmtNum(p.allRsi, 1)}</div>
      <div>All 24h %: {fmtChangePct(p.allChange)}</div>
      <div>Points (7d): {fmtInt(p.hitCount)}</div>
      <div>
        Δ RSI:{" "}
        {Number.isFinite(p.deltaRsi)
          ? `${p.deltaRsi > 0 ? "+" : ""}${fmtNum(p.deltaRsi, 1)}`
          : "—"}
      </div>
    </div>
  );
}


export default function CategoryHypeCharts({
  initialDays = 30,
  maxRsiCategories = 14,
}) {
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal
    ? "http://localhost:5000"
    : "https://www.trendscout.club";

  const [days, setDays] = React.useState(initialDays);

  // Part A data
  const [heatmap, setHeatmap] = React.useState({
    from: null,
    to: null,
    items: [],
  });
  const [loadingHeat, setLoadingHeat] = React.useState(false);
  const [errorHeat, setErrorHeat] = React.useState(null);

  // Part B data
  const [rsiSnapshot, setRsiSnapshot] = React.useState({
    asOf: null,
    items: [],
  });
  const [loadingRsi, setLoadingRsi] = React.useState(false);
  const [errorRsi, setErrorRsi] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [viewMode, setViewMode] = React.useState("rsi"); // "rsi" | "change"
  // admin check for manual refresh
  const userId = localStorage.getItem("userId");
  const isAdmin = userId === "pziPDtOgJiX06oGuJxNwxE";

  // ---------- Fetch Part A ----------
  React.useEffect(() => {
    let cancelled = false;
    async function loadHeat() {
      setLoadingHeat(true);
      setErrorHeat(null);
      try {
        const url = `${baseUrl}/api/categories/hype-heatmap?days=${days}`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (cancelled) return;
        setHeatmap(j || { items: [] });
      } catch (e) {
        if (cancelled) return;
        console.error("heatmap load error", e);
        setErrorHeat(e.message || "Failed to load heatmap");
        setHeatmap({ from: null, to: null, items: [] });
      } finally {
        if (!cancelled) setLoadingHeat(false);
      }
    }
    loadHeat();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, days]);

  // ---------- Fetch Part B (snapshot from DB) ----------
  const loadRsi = React.useCallback(async () => {
    setLoadingRsi(true);
    setErrorRsi(null);
    try {
      const params = new URLSearchParams({
        days: String(days),
        limit: String(maxRsiCategories),
      });
      const url = `${baseUrl}/api/categories/hype-rsi?${params.toString()}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRsiSnapshot(j || { items: [] });
    } catch (e) {
      console.error("rsi snapshot load error", e);
      setErrorRsi(e.message || "Failed to load category RSI snapshot");
      setRsiSnapshot({ asOf: null, items: [] });
    } finally {
      setLoadingRsi(false);
    }
  }, [baseUrl, days, maxRsiCategories]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadRsi();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRsi]);

    // ---------- Derived: performance scatter (Part B) ----------
const perfScatterData = React.useMemo(() => {
  if (!Array.isArray(rsiSnapshot.items)) return [];

  return rsiSnapshot.items
    .map((c) => {
      const buckets = computeChangeBuckets(c.sampleTokens);
      const allChange = buckets.allChange;

      return {
        name: c.name,
        abbr: abbrevFromName(c.name),
        allRsi: Number(c.allWeightedRsi ?? NaN),
        allChange,
        hitCount: Number(c.hitCount || 0),   // Points (7d)
        deltaRsi: Number(c.deltaRsi ?? NaN), // 👈 add this
      };
    })
    .filter(
      (p) =>
        Number.isFinite(p.allRsi) &&
        Number.isFinite(p.allChange)
    );
}, [rsiSnapshot.items]);


    const perfZRange = React.useMemo(() => {
    const maxPts = perfScatterData.reduce(
      (m, p) => Math.max(m, p.hitCount || 0),
      0
    );
    if (!maxPts || maxPts <= 0) return [40, 120];
    if (maxPts < 5) return [60, 160];
    if (maxPts < 15) return [80, 220];
    return [90, 260];
  }, [perfScatterData]);



  // ---------- Admin manual refresh (calls /refresh with auth) ----------
const refreshNow = React.useCallback(async () => {
  if (!isAdmin) return;
  setRefreshing(true);
  try {
    const token = localStorage.getItem("token");
    await fetch(`${baseUrl}/api/categories/hype-rsi/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ days, limit: maxRsiCategories }),
    });
    // After job completes, fetch snapshot from DB
    await loadRsi();
  } catch (e) {
    console.error("rsi snapshot refresh error", e);
    alert(e.message || "Refresh failed (see console)");
  } finally {
    setRefreshing(false);
  }
}, [baseUrl, days, maxRsiCategories, isAdmin, loadRsi]);


  // ---------- Derived: scatter points ----------
  const scatterData = React.useMemo(() => {
    if (!Array.isArray(heatmap.items)) return [];
    return heatmap.items.map((c) => ({
      name: c.name,
      abbr: abbrevFromName(c.name),
      slug: c.slug,
      lastHitAt: c.lastHitAt,
      lastEpoch: c.lastHitAt ? new Date(c.lastHitAt).getTime() : null,
      tokenCount: c.tokenCount || 0,
      hitCount: c.hitCount || 0,
      avgScore: c.avgScore,
      pointCount7d: c.pointCount7d,
    }));
  }, [heatmap.items]);

  const maxHitCount = React.useMemo(
    () =>
      scatterData.reduce(
        (m, p) => Math.max(m, Number(p.hitCount || 0)),
        0
      ),
    [scatterData]
  );

  const rsiDomain = React.useMemo(() => {
  if (!perfScatterData.length) return [0, 100];

  const values = perfScatterData
    .map(p => Number(p.allRsi))
    .filter(v => Number.isFinite(v));

  if (!values.length) return [0, 100];

  const min = Math.min(...values);
  const max = Math.max(...values);

  // Add ±5 padding and clamp to 0–100
  const paddedMin = Math.max(0, Math.floor(min - 5));
  const paddedMax = Math.min(100, Math.ceil(max + 5));

  return [paddedMin, paddedMax];
}, [perfScatterData]);


  const zRange = React.useMemo(() => {
    if (!maxHitCount || maxHitCount <= 0) return [40, 120];
    if (maxHitCount < 5) return [60, 160];
    if (maxHitCount < 15) return [80, 220];
    return [90, 260];
  }, [maxHitCount]);

  const timeDomain = React.useMemo(() => {
    if (!scatterData.length) return ["auto", "auto"];
    let min = Infinity;
    let max = -Infinity;
    scatterData.forEach((p) => {
      if (!p.lastEpoch) return;
      if (p.lastEpoch < min) min = p.lastEpoch;
      if (p.lastEpoch > max) max = p.lastEpoch;
    });
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return ["auto", "auto"];
    }
    const pad = (max - min) * 0.05 || 60 * 60 * 1000;
    return [min - pad, max + pad];
  }, [scatterData]);

  // ---------- UI ----------
  return (
    <div className="ai-suggestion-list" style={{ marginTop: 16 }}>
      <div className="macro-section">
        {/* Header + window selector */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ margin: 0 }}>Category Rotation</h3>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ opacity: 0.8 }}>Window:</span>
            {TIME_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => setDays(p.days)}
                className={days === p.days ? "pill pill-yellow" : "pill"}
                style={{ cursor: "pointer" }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* PART A */}
        <div style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 8, textAlign: "center" }}>
            Hype scatter by category
          </h4>

          {loadingHeat && (
            <div className="loading-banner">Loading heatmap…</div>
          )}
          {errorHeat && <div className="error-banner">{errorHeat}</div>}
          {!loadingHeat && !errorHeat && scatterData.length === 0 && (
            <div className="subtle-note">
              No hype categories in the selected window.
            </div>
          )}

          {!loadingHeat && !errorHeat && scatterData.length > 0 && (
            <div
              style={{
                width: "100%",
                height: 360,
                background:
                  "linear-gradient(to top, rgba(34,197,94,0.06), rgba(15,23,42,1) 45%, rgba(248,113,113,0.08))",
                borderRadius: 12,
                padding: 8,
              }}
            >
              <ResponsiveContainer>
                <ScatterChart
                  margin={{ top: 16, right: 24, bottom: 24, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis
                    type="number"
                    dataKey="lastEpoch"
                    name="Last Hit"
                    domain={timeDomain}
                    tickFormatter={(v) => fmtDateShort(new Date(v))}
                    tick={{ fill: "#9ca3af", fontSize: 11 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="tokenCount"
                    name="Tokens"
                    tickCount={7}
                    allowDecimals={false}
                    tick={{ fill: "#9ca3af", fontSize: 11 }}
                  />
                  <ZAxis
                    type="number"
                    dataKey="hitCount"
                    range={zRange}
                    name="Hits"
                  />
                        <Tooltip
                        cursor={{ strokeDasharray: "3 3" }}
                        content={<CategoryTooltip />}
                        />


                  <Legend />
                  <Scatter
                    name="Categories"
                    data={scatterData}
                    shape={renderBubble} // 👈 bubbles with abbreviations
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* PART B */}
        <div style={{ marginTop: 24 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div>
                  <h4 style={{ marginBottom: 4 }}>
                    Global context (category {viewMode === "rsi" ? "RSI" : "24h change"})
                  </h4>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {/* View toggle */}
                  <div
                    style={{
                      display: "inline-flex",
                      padding: 2,
                      borderRadius: 999,
                      border: "1px solid #4b5563",
                      background: "#020617",
                      fontSize: 11,
                    }}
                  >
                    <button
                      onClick={() => setViewMode("rsi")}
                      style={{
                        padding: "3px 8px",
                        borderRadius: 999,
                        border: "none",
                        background: viewMode === "rsi" ? "#eab308" : "transparent",
                        color: viewMode === "rsi" ? "#020617" : "#e5e7eb",
                        cursor: "pointer",
                      }}
                    >
                      RSI
                    </button>
                    <button
                      onClick={() => setViewMode("change")}
                      style={{
                        padding: "3px 8px",
                        borderRadius: 999,
                        border: "none",
                        background: viewMode === "change" ? "#eab308" : "transparent",
                        color: viewMode === "change" ? "#020617" : "#e5e7eb",
                        cursor: "pointer",
                      }}
                    >
                      24h %
                    </button>
                  </div>

                  {/* Admin refresh button */}
                  {isAdmin && (
                    <button
                      onClick={refreshNow}
                      disabled={refreshing}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 999,
                        border: "1px solid #4b5563",
                        background: refreshing ? "#111827" : "#0f172a",
                        color: "#e5e7eb",
                        fontSize: 12,
                        cursor: refreshing ? "default" : "pointer",
                        boxShadow: "0 0 0 1px rgba(56,189,248,0.4)",
                      }}
                      title="Force an immediate RSI rescan (admin only)"
                    >
                      {refreshing ? "Refreshing…" : "Refresh now"}
                    </button>
                  )}
                </div>
              </div>

          {loadingRsi && (
            <div className="loading-banner">Loading category RSI…</div>
          )}
          {errorRsi && <div className="error-banner">{errorRsi}</div>}
          {!loadingRsi &&
            !errorRsi &&
            (!rsiSnapshot.items || rsiSnapshot.items.length === 0) && (
              <div className="subtle-note">
                No RSI snapshot available for the selected window.
              </div>
            )}

          {!loadingRsi &&
            !errorRsi &&
            rsiSnapshot.items &&
            rsiSnapshot.items.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 11,
                    opacity: 0.7,
                    marginBottom: 4,
                    textAlign: "right",
                  }}
                >
                  As of:{" "}
                  {rsiSnapshot.asOf
                    ? fmtDateShort(rsiSnapshot.asOf)
                    : "—"}
                  {" • "}
                  Window: {rsiSnapshot.windowDays || days}d
                </div>

                {rsiSnapshot.items && rsiSnapshot.items.length > 0 && (
                <CategoryRsiSummary items={rsiSnapshot.items} />
                )}

                      {/* New scatter: All RSI vs All 24h % for same categories as the table */}
                {perfScatterData.length > 0 && (
                  <div
                    style={{
                      width: "100%",
                      height: 320,
                      background:
                        "linear-gradient(to top, rgba(34,197,94,0.06), rgba(15,23,42,1) 45%, rgba(248,113,113,0.08))",
                      borderRadius: 12,
                      padding: 8,
                      marginBottom: 16,
                    }}
                  >
                    <h5 style={{ margin: "0 0 4px 4px", fontSize: 12, opacity: 0.8 }}>
                      Category momentum map (All RSI vs All 24h %, bubble size = Points 7d)
                    </h5>
                    <ResponsiveContainer>
                      <ScatterChart
                        margin={{ top: 16, right: 24, bottom: 24, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis
                          type="number"
                          dataKey="allRsi"
                          name="All RSI"
                          domain={rsiDomain}
                          tick={{ fill: "#9ca3af", fontSize: 11 }}
                        />
                        <YAxis
                          type="number"
                          dataKey="allChange"
                          name="All 24h %"
                          tickFormatter={(v) => fmtChangePct(v)}
                          tick={{ fill: "#9ca3af", fontSize: 11 }}
                        />
                        <ZAxis
                          type="number"
                          dataKey="hitCount"
                          name="Points (7d)"
                          range={perfZRange}
                        />
                        <Tooltip
                          cursor={{ strokeDasharray: "3 3" }}
                          content={<PerfTooltip />}
                        />
                        <Legend
                            formatter={(value) =>
                              value === "Categories"
                                ? "Categories (color = Δ RSI, size = Points 7d)"
                                : value
                            }
                          />
                        <Scatter
                          name="Categories"
                          data={perfScatterData}
                          shape={renderPerfBubble}
                        />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                )}


                <div className="table-wrap">
                  <table className="hype-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Points (7d)</th>
                        <th>Tokens (hype)</th>
                        <th>{viewMode === "rsi" ? "Core RSI (4)" : "Core 24h %"}</th>
                        <th>{viewMode === "rsi" ? "Sprinters RSI (4)" : "Sprinters 24h %"}</th>
                        <th>{viewMode === "rsi" ? "All RSI" : "All 24h %"}</th>
                        <th>Δ RSI</th>
                        <th>Trend</th>
                        <th>Tokens (click for chart)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rsiSnapshot.items.map((c, idx) => (
                        <tr key={c.slug || c.name || idx}>
                          <td>{c.name}</td>
                          <td>{fmtInt(c.hitCount)}</td>
                          <td>{fmtInt(c.tokenCount)}</td>
                        {(() => {
                          const buckets = computeChangeBuckets(c.sampleTokens);
                          const coreChange = buckets.coreChange;
                          const sprChange = buckets.sprChange;
                          const allChange = buckets.allChange;

                          return (
                            <>
                              {/* Core column */}
                              <td>
                                {viewMode === "rsi" ? (
                                  <span
                                    style={{
                                      color: rsiBandColor(c.coreWeightedRsi),
                                    }}
                                  >
                                    {fmtNum(c.coreWeightedRsi, 1)}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      color: changeColor(coreChange),
                                    }}
                                  >
                                    {fmtChangePct(coreChange)}
                                  </span>
                                )}
                              </td>

                              {/* Sprinters column */}
                              <td>
                                {viewMode === "rsi" ? (
                                  <span
                                    style={{
                                      color: rsiBandColor(c.sprinterWeightedRsi),
                                    }}
                                  >
                                    {fmtNum(c.sprinterWeightedRsi, 1)}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      color: changeColor(sprChange),
                                    }}
                                  >
                                    {fmtChangePct(sprChange)}
                                  </span>
                                )}
                              </td>

                              {/* All column */}
                              <td>
                                {viewMode === "rsi" ? (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: rsiBandColor(c.allWeightedRsi),
                                    }}
                                  >
                                    {fmtNum(c.allWeightedRsi, 1)}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: changeColor(allChange),
                                    }}
                                  >
                                    {fmtChangePct(allChange)}
                                  </span>
                                )}
                              </td>
                            </>
                          );
                        })()}

                              <td style={{ color: c.deltaRsi > 0 ? "#22c55e" : c.deltaRsi < 0 ? "#f97316" : "#e5e7eb" }}>
                                {c.deltaRsi == null ? "—" : `${c.deltaRsi > 0 ? "+" : ""}${fmtNum(c.deltaRsi, 1)}`}
                            </td>

                            {/* Trend label */}
                            <td>
                                {c.trendLabel ? (
                                <span className={`trend-pill trend-${c.trendLabel}`}>
                                    {c.trendLabel.charAt(0).toUpperCase() + c.trendLabel.slice(1)}
                                </span>
                                ) : (
                                "—"
                                )}
                            </td>
                            <td>
                              {Array.isArray(c.sampleTokens) && c.sampleTokens.length > 0 ? (
                                <div
                                  className="category-token-row"
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 6,
                                  }}
                                >
                                  {c.sampleTokens.map((t) => {
                                    // base symbol from sample token
                                    const baseSym = String(t.symbol || "")
                                      .toUpperCase()
                                      .replace(/[^A-Z0-9]/g, "")
                                      .replace(/USDT$/, "");

                                    // hype bases from backend (Category.tokens.base)
                                    const hypeSet = new Set(
                                      (c.hypeTokenBases || []).map(s => String(s).toUpperCase())
                                    );

                                    const isHypeToken = hypeSet.has(baseSym);

                                    const colorValue =
                                      viewMode === "rsi"
                                        ? rsiBandColor(t.rsi)
                                        : changeColor(t.priceChange24h);

                                    return (
                                        <button
                                          key={t.id || t.symbol}
                                          className={`pill ${isHypeToken ? "gloss-pill" : ""}`}
                                          onClick={() => openTokenChart(t.symbol)}
                                          style={{
                                            fontSize: 11,
                                            cursor: "pointer",
                                            borderColor: colorValue,
                                            color: colorValue,
                                            padding: "3px 8px",
                                          }}
                                          title={`${t.name} — RSI ${fmtNum(t.rsi, 1)}, 24h ${fmtNum(t.priceChange24h, 1)}%`}
                                        >
                                        {String(t.symbol || "").toUpperCase()}{" "}
                                        (
                                          {viewMode === "rsi"
                                            ? fmtNum(t.rsi, 1)
                                            : fmtChangePct(t.priceChange24h)}
                                        )
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
}
